import Chart, { ChartOptions } from "chart.js/auto";
import "chartjs-adapter-moment";
import { observable } from "mobx";
import moment from "moment";

import { assert, parseDatetimeToLocal } from "../utils/utils";
import { applyFilters, applySort, DataModel } from "./data-model";
import { EnumFilter } from "./data-model/enum-filter";
import { Filter } from "./data-model/filter";
import { Sort } from "./data-model/sort";

type PlotKey = Record<string, string>;

export class SimplePlotModel<T extends object = any> {
  data: DataModel;
  @observable accessor valueKey = "";
  @observable accessor filterKey = "";
  @observable accessor dateTimeKey = "";
  chartId: string;
  @observable accessor hueKey: PlotKey | null = null;
  @observable accessor styleKey: PlotKey | null = null;
  @observable accessor plotType: string | null = null;
  @observable accessor plotStart: string = "";
  @observable accessor plotEnd: string = "";
  @observable accessor plotTimeSeries: any[] = [];
  @observable accessor chart: any = null;
  @observable accessor showLegend = false;

  defaultColor = "rgba(187, 22, 163, 1)";
  defaultStyle = "circle";
  @observable accessor extraFilters: Filter<T>[] = [];

  minDataPoints = 2;
  @observable accessor plotTypeOptions: string[] = [];
  defaultPlotTypeOptions: string[];

  constructor(
    dataModel: DataModel,
    plotTypeOptions: string[],
    chartId: string,
  ) {
    this.data = dataModel;
    this.chartId = chartId;
    this.defaultPlotTypeOptions = plotTypeOptions;
  }

  private getData(filterOption: string) {
    const sort = new Sort(this.dateTimeKey, "datetime", "asc");

    const filters: Filter<any>[] = [
      new EnumFilter(this.filterKey, [filterOption]),
      ...this.extraFilters,
    ];

    return applySort(applyFilters(this.data.data, filters), sort);
  }

  initPlot() {
    this.plotTypeOptions = [];

    for (const plotOption of this.defaultPlotTypeOptions) {
      const filteredData = this.getData(plotOption);

      if (filteredData.length >= this.minDataPoints) {
        this.plotTypeOptions.push(plotOption);
      }
    }
  }

  resetPlot() {
    if (this.chart) {
      this.chart.destroy();
    }
    this.chart = null;
    const canvas = document.getElementById(this.chartId) as HTMLCanvasElement;
    if (canvas) {
      canvas.style.display = "none";
    }
  }

  preparePlot() {
    this.resetPlot();
    const plotType = this.plotType;
    assert(plotType != null, "plotType is not set");
    const filteredData = this.getData(plotType);

    this.plotStart = parseDatetimeToLocal(
      new Date(
        Math.min(
          ...filteredData.map((item) =>
            new Date(item[this.dateTimeKey] as string).getTime(),
          ),
        ),
      ),
    );
    this.plotEnd = parseDatetimeToLocal(
      new Date(
        Math.max(
          ...filteredData.map((item) =>
            new Date(item[this.dateTimeKey] as string).getTime(),
          ),
        ),
      ),
    );
    this.plotTimeSeries = filteredData;
    this.updatePlot(this.plotStart, this.plotEnd);
  }

  yLabel(plotType: string) {
    return plotType;
  }

  getChartScales(): ChartOptions<"line">["scales"] {
    return {
      x: {
        type: "time",
        time: {
          unit: "day",
          displayFormats: {
            day: "MMM D",
          },
        },
        adapters: {
          date: {
            library: moment,
          },
        },
        title: {
          display: true,
          text: "date",
          font: {
            size: 16,
            weight: "bold",
          },
        },
      },
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: this.plotType != null ? this.yLabel(this.plotType) : "",
          font: {
            size: 16,
            weight: "bold",
          },
        },
      },
    };
  }

  getDatesTimeSeries(minDate: string, maxDate: string) {
    const filtPlotEnd = new Date(maxDate);
    filtPlotEnd.setSeconds(filtPlotEnd.getSeconds() + 1);
    return this.plotTimeSeries.filter(
      (item) =>
        new Date(item[this.dateTimeKey]) >= new Date(minDate) &&
        new Date(item[this.dateTimeKey]) <= filtPlotEnd,
    );
  }

  chartData(
    plotData: Record<string, any>[],
    color: string,
    style: string,
    label?: string,
    hidden: boolean = false,
  ) {
    return {
      label: label,
      data: plotData.map((item) => ({
        x: item[this.dateTimeKey],
        y: item[this.valueKey],
      })),
      borderColor: color,
      backgroundColor: color,
      pointStyle: style,
      borderWidth: 0.5,
      fill: false,
      pointRadius: 5,
      pointHoverRadius: 7,
      hidden: hidden,
    };
  }

  getDatasets(timeSeries: Record<string, any>[]) {
    return [this.chartData(timeSeries, this.defaultColor, this.defaultStyle)];
  }

  updatePlot(minDate: string, maxDate: string) {
    this.resetPlot();

    const timeSeries = this.getDatesTimeSeries(minDate, maxDate);
    const canvas = document.getElementById(this.chartId) as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    assert(ctx != null, "Cannot get 2D context of the plot canvas");
    const datasets = this.getDatasets(timeSeries);
    this.chart = new Chart(ctx, {
      type: "line",
      data: {
        datasets: datasets,
      },
      options: {
        plugins: {
          legend: {
            display: this.showLegend,
            labels: {
              usePointStyle: true,
            },
          },
        },
        scales: this.getChartScales(),
      },
    });
    canvas.style.display = "block";
  }
}

export class SingleGroupedPlotModel extends SimplePlotModel {
  // palettes
  colorPalette = [
    "rgba(187, 22, 163, 1)",
    "rgba(5, 195, 222, 1)",
    "rgba(37, 40, 42, 1)",
    "rgba(0, 146, 203, 1)",
    "rgba(112, 115, 114, 1)",
  ];
  stylePalette = ["circle", "triangle", "cross", "rect"];
  @observable accessor defaultHidden: string[] = [];

  constructor(dataModel: any, plotTypeOptions: string[], chartId: string) {
    super(dataModel, plotTypeOptions, chartId);
    this.showLegend = true;
  }

  /**
   * Display text for a raw hue value — identity here, overridden by subclasses
   * that need a localized legend. `colorMapping` and `defaultHidden` keep
   * keying on the *raw* hue, so an override can never change which colour a
   * series gets or which series start hidden.
   */
  hueLabel(hue: string): string {
    return hue;
  }

  singleGroup(timeSeries: Record<string, any>[], key: string) {
    return timeSeries.reduce<Record<string, any[]>>((acc, item) => {
      if (!acc[item[key]]) {
        acc[item[key]] = [];
      }
      acc[item[key]].push(item);
      return acc;
    }, {});
  }

  getDatasets(timeSeries: Record<string, any>[]) {
    const plotType = this.plotType;
    const hueAttr = plotType != null ? this.hueKey?.[plotType] : undefined;
    const styleAttr = plotType != null ? this.styleKey?.[plotType] : undefined;
    if (hueAttr) {
      const groupedData = this.singleGroup(timeSeries, hueAttr);
      return Object.keys(groupedData).map((key, index) => {
        const groupData = groupedData[key];
        const color = this.colorPalette[index % this.colorPalette.length];
        return this.chartData(
          groupData,
          color,
          this.defaultStyle,
          this.hueLabel(key),
        );
      });
    } else if (styleAttr) {
      const groupedData = this.singleGroup(timeSeries, styleAttr);
      return Object.keys(groupedData).map((key, index) => {
        const groupData = groupedData[key];
        const style = this.stylePalette[index % this.stylePalette.length];
        return this.chartData(
          groupData,
          this.defaultColor,
          style,
          key,
          this.defaultHidden.includes(key),
        );
      });
    }
    return [];
  }
}

export class DoubleGroupedPlotModel extends SingleGroupedPlotModel {
  @observable accessor colorMapping: Record<string, string> = {};
  @observable accessor styleMapping: Record<string, string> = {};
  @observable override accessor defaultHidden: string[] = [];

  constructor(dataModel: any, plotTypeOptions: string[], chartId: string) {
    super(dataModel, plotTypeOptions, chartId);
  }

  doubleGroup(timeSeries: Record<string, any>[], key1: string, key2: string) {
    return timeSeries.reduce<Record<string, any[]>>((acc, item) => {
      const key = `${item[key1]}_${item[key2]}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});
  }

  getDatasets(timeSeries: Record<string, any>[]) {
    const plotType = this.plotType;
    assert(plotType != null, "plotType is not set");
    assert(this.hueKey != null, "hueKey is not configured");
    assert(this.styleKey != null, "styleKey is not configured");
    const groupedData = this.doubleGroup(
      timeSeries,
      this.hueKey[plotType],
      this.styleKey[plotType],
    );
    return Object.keys(groupedData).map((key, index) => {
      const groupData = groupedData[key];
      const [hue, style] = key.split("_");
      // The colour map and `defaultHidden` are keyed on the raw hue; only the
      // dataset label — which Chart.js draws in the legend and tooltip — goes
      // through `hueLabel`.
      return this.chartData(
        groupData,
        this.colorMapping[hue],
        this.styleMapping[style],
        `${this.hueLabel(hue)} - ${style}`,
        this.defaultHidden.includes(`${hue} - ${style}`),
      );
    });
  }
}

export class MedicalPlotModel extends SimplePlotModel {
  constructor(dataModel: any, plotTypeOptions: string[], chartId: string) {
    super(dataModel, plotTypeOptions, chartId);
    this.valueKey = "measurement_value";
    this.filterKey = "measurement_type";
    this.dateTimeKey = "measurement_datetime";
  }
}

export class SymptomPlotModel extends SimplePlotModel {
  constructor(dataModel: any, plotTypeOptions: string[], chartId: string) {
    super(dataModel, plotTypeOptions, chartId);
    this.valueKey = "symptom_value";
    this.filterKey = "symptom_name";
    this.dateTimeKey = "symptom_datetime";
  }
}
